import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Sparkles, Coffee, ShoppingBag, Briefcase } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Bullets } from "@/components/Bits";

const STATUS_STYLES = {
  available: "text-emerald-600 dark:text-emerald-400 border-emerald-600/30 bg-emerald-500/10",
  soon: "text-orange-600 dark:text-orange-400 border-orange-600/30 bg-orange-500/10",
};

const POS = [
  { name: "Square", type: "POS integration", status: "Available today", tone: "available", testId: "business-pos-square-badge" },
  { name: "Clover", type: "POS integration", status: "Available today", tone: "available", testId: "business-pos-clover-badge" },
  { name: "Toast", type: "POS integration", status: "In progress", tone: "soon", testId: "business-pos-toast-badge" },
];

const BULLETS = [
  "Get discovered on Discover",
  "Digital & QR/table ordering",
  "Payments powered by Stripe with clear payouts",
  "Split Bill & tipping built in (MONOE takes 0% of tips)",
  "Optional Rewards you design and fund (incl. birthday) with analytics",
  "Order management + shared staff terminal with granular permissions",
  "Business analytics across sales, orders, rewards",
  "Multi-location & franchise management",
  "POS integrations — Square and Clover today, Toast in progress",
];

const VERTICALS = [
  { icon: Sparkles, name: "Nail salons & beauty" },
  { icon: Coffee, name: "Cafés" },
  { icon: ShoppingBag, name: "Retail" },
  { icon: Briefcase, name: "Professional & service" },
];

const POS_FILTERS = [
  { id: "all", label: "All" },
  { id: "available", label: "Available" },
  { id: "soon", label: "In progress" },
];

const BusinessHero = () => (
  <section className="relative overflow-hidden border-b border-border">
    <div className="pointer-events-none absolute inset-0 grid-bg opacity-50 [mask-image:radial-gradient(ellipse_at_top,black,transparent_75%)]" />
    <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <Reveal><Eyebrow>For businesses</Eyebrow></Reveal>
      <Reveal delay={0.05}>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
          Reach customers. Take orders. Get paid. Reward loyalty.
        </h1>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">
          MONOE can work alongside your existing POS where supported.
        </p>
      </Reveal>
    </div>
  </section>
);

const Verticals = () => (
  <section className="border-t border-border bg-secondary/40">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <Reveal>
        <Eyebrow>Not restaurant-only</Eyebrow>
        <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl">
          Built for all kinds of local businesses.
        </h2>
      </Reveal>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {VERTICALS.map((v, i) => (
          <Reveal key={v.name} delay={i * 0.04}>
            <div className="h-full rounded-lg border border-border bg-card p-6">
              <v.icon className="h-5 w-5 text-foreground" />
              <p className="mt-4 font-display text-base font-semibold">{v.name}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Each gets discovered, takes payment/tips, and can reward repeat visits. Each business
          only turns on the features that fit — rewards, reservations, and ordering are all
          optional.
        </p>
      </Reveal>
    </div>
  </section>
);

const PosCard = ({ pos }) => (
  <div data-testid={pos.testId} className="h-full rounded-lg border border-border bg-card p-6">
    <span className="font-display text-lg font-semibold">{pos.name}</span>
    <p className="mt-1 text-xs text-muted-foreground">{pos.type}</p>
    <span className={`mt-4 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono-ui text-[11px] ${STATUS_STYLES[pos.tone]}`}>
      {pos.tone === "available" && <CheckCircle2 className="h-3 w-3" />}
      {pos.status}
    </span>
  </div>
);

const PosIntegrations = () => {
  const [filter, setFilter] = useState("all");
  const visible = POS.filter((p) =>
    filter === "all" ? true : filter === "available" ? p.tone === "available" : p.tone !== "available"
  );

  return (
    <section className="border-t border-border">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
        <Reveal>
          <Eyebrow>Integrations</Eyebrow>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl">
            Works with the POS you already use.
          </h2>
          <p className="mt-4 max-w-xl text-sm text-muted-foreground">
            Integration availability varies by provider approval and region. Names indicate current
            or in-progress compatibility and do not imply endorsement or sponsorship.
          </p>
        </Reveal>

        <div className="mt-8 inline-flex flex-wrap gap-2">
          {POS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              data-testid={`pos-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                filter === f.id ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:bg-accent"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.04}>
              <PosCard pos={p} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const BusinessCta = () => (
  <section className="border-t border-border">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <Reveal>
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <p className="font-display text-xl font-semibold">Interested in bringing MONOE to your business?</p>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-md bg-foreground px-6 py-3 text-sm font-semibold text-background">
            Get in touch <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Business = () => {
  useSeo({
    title: "For business",
    description: "Reach customers, take orders, get paid, and reward loyalty. MONOE works alongside your existing POS where supported.",
    path: "/business",
  });

  return (
    <div>
      <BusinessHero />
      <section>
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Reveal><Bullets items={BULLETS} testId="business-bullets" /></Reveal>
        </div>
      </section>
      <Verticals />
      <PosIntegrations />
      <BusinessCta />
    </div>
  );
};

export default Business;
